/**
 * Builds the POST /leads/intake payload from New Patient form state and the
 * resolved field settings (KTD6, KTD7).
 *
 *  - formMode is always sent; the server checks it (KTD4).
 *  - A field shown in this mode sends all of its payload keys. A patient text
 *    or code field the user emptied is sent as '' so the existing-patient
 *    update clears it (the server writes only the keys it receives).
 *  - A hidden field sends nothing, so a stored value is never overwritten and
 *    the server can apply the field's default to new rows. The one exception
 *    is convert_lead: a hidden field holding a non-empty value prefilled from
 *    the lead is carried into the new patient and plan (R9).
 *  - Lead/patient ids, the duplicate override and the shared-patient link
 *    are built exactly as NewLeadPage's handleSubmit built them before
 *    settings existed. The dial code and opt-ins follow the Cell Phone and
 *    Email inputs that hold them: sent only when that input is shown.
 *  - The referring patient follows the category (referral_select_1_anchor),
 *    whose renderer holds the Referring Patient picker.
 *
 * INTAKE_PAYLOAD_KEYS mirrors IntakeFieldRules::FIELD_MAP
 * (api/app/Services/NewPatientForm/IntakeFieldRules.php). id_scan has no
 * payload key: the scan uploads on its own.
 */
import type { SubmitLeadIntakePayload } from '@/services/leadIntake.service';
import { fieldValue, isEmpty, isShown, type FormMode, type FormState, type ResolvedField } from './newPatientFormRules';

/** The ADDRESS country fallback (a countryID), as NewLeadPage uses today. */
export const DEFAULT_COUNTRY_ID = 1;

export interface IntakeContext {
  /** Lead being converted, if any. */
  leadId: number | null;
  /** Existing patient the plan is for (Add Plan route or a match-panel pick). */
  patientId: number | null;
  /** Shared patient picked in the match panel; sent only when patientId is null. */
  masterPatientId: number | null;
  allowDuplicatePlanType: boolean;
  /** The chosen lead source category is the "established patient" referrer. */
  isEstablishedPatientSource: boolean;
  countryCodes: ReadonlyArray<{ countryAbbr: string | null; countryID: number }>;
}

export interface BuildIntakePayloadArgs {
  state: FormState;
  fields: readonly ResolvedField[];
  mode: FormMode;
  /** Catalog keys whose current value was prefilled from the lead. */
  leadPrefilledKeys: ReadonlySet<string>;
  context: IntakeContext;
}

type PayloadPart = Partial<SubmitLeadIntakePayload>;
type PartBuilder = (state: FormState, context: IntakeContext) => PayloadPart;

const text = (value: string) => value.trim();
const optionalText = (value: string) => value.trim() || undefined;

/** True when an established-patient category still has no referring patient. */
const referralOnHold = (state: FormState, context: IntakeContext) =>
  context.isEstablishedPatientSource && !state.referralPatient;

/** Catalog key → the payload keys it fills, built from form state. */
export const INTAKE_PAYLOAD_KEYS: Record<string, PartBuilder> = {
  // Patient Information
  first_name: (s) => ({ firstName: text(s.firstName), lastName: text(s.lastName) }),
  middle_name: (s) => ({ middleName: text(s.middleName) }),
  maiden_name: (s) => ({ maidenName: text(s.maidenName) }),
  gender: (s) => ({ gender: s.gender ?? '' }),
  // Never the legacy "0000-00-00" placeholder: the server's date rule rejects it.
  birth_date: (s) => ({ birthDate: s.birthDate && !s.birthDate.startsWith('0000') ? s.birthDate : '' }),
  // Contact Information. A blank email or cell is left out, never sent blank:
  // the server treats them as sign-in contacts that a blank must not clear.
  cell_phone: (s) => ({ cellPhone: optionalText(s.cellPhone) }),
  email: (s) => ({ email: optionalText(s.email) }),
  address: (s) => ({ address: text(s.address) }),
  city: (s) => ({ city: text(s.city) }),
  state: (s) => ({ state: s.state }),
  zip: (s) => ({ zip: text(s.zip) }),
  country: (s, c) => ({
    countryId: c.countryCodes.find((code) => code.countryAbbr === s.addressCountry)?.countryID ?? DEFAULT_COUNTRY_ID,
  }),
  // Plan Setup. Plan columns only land on the new plan row, so an emptied
  // dropdown is left out exactly as today (omitted and blank write the same).
  plan_type: (s) => ({ planTypeId: s.planTypeId ?? undefined }),
  facility: (s) => ({ facilityId: s.facilityId ?? undefined }),
  // The Referring Patient picker renders with the category (the locked
  // anchor's renderer), so the referring patient goes out with the category
  // even when Lead Source is hidden.
  referral_select_1_anchor: (s, c) => {
    const referring = c.isEstablishedPatientSource && s.referralPatient ? s.referralPatient : null;
    return {
      leadSourceId: referralOnHold(s, c) ? undefined : (s.referralCategoryId ?? undefined),
      // The referring patient's own id, so the plan's refSourceID is a patientID.
      referringPatientId: referring ? referring.patientId : undefined,
      referralFirstname: referring ? referring.firstName : undefined,
      referralLastname: referring ? referring.lastName : undefined,
    };
  },
  referral_name: (s, c) => ({
    referralSourceId: referralOnHold(s, c) ? undefined : (s.referralSourceId ?? undefined),
  }),
  doctor: (s) => ({ doctorId: s.doctorId ?? undefined }),
  coordinator: (s) => ({ coordinatorId: s.coordinatorId ?? undefined }),
  nurse: (s) => ({ nurseId: s.nurseId ?? undefined }),
  // Interests
  ball_park_given: (s) => ({ revenueOpportunity: s.revenueOpportunity.trim() ? Number(s.revenueOpportunity) : undefined }),
  ball_park: (s) => ({ commitmentLevel: s.commitmentLevel ?? undefined }),
  timeframe: (s) => ({ timeframe: s.timeframe ?? undefined }),
  // Notes
  notes: (s) => ({ notes: text(s.notes) }),
};

/** Whether this field's payload keys go out with the save. */
function sendsField(field: ResolvedField, args: BuildIntakePayloadArgs): boolean {
  if (isShown(field, args.mode)) return true;
  return (
    args.mode === 'convert_lead'
    && field.available
    && args.leadPrefilledKeys.has(field.key)
    && !isEmpty(field.kind, fieldValue(field.key, args.state))
  );
}

export function buildIntakePayload(args: BuildIntakePayloadArgs): SubmitLeadIntakePayload {
  const { state, context } = args;

  const payload: SubmitLeadIntakePayload = {
    formMode: args.mode,
    leadId: context.leadId ?? undefined,
    patientId: context.patientId ?? undefined,
    masterPatientId: context.patientId === null && context.masterPatientId !== null ? context.masterPatientId : undefined,
    allowDuplicatePlanType: context.allowDuplicatePlanType || undefined,
  };

  for (const field of args.fields) {
    const build = INTAKE_PAYLOAD_KEYS[field.key];
    if (!build || !sendsField(field, args)) continue;
    Object.assign(payload, build(state, context));
  }

  // The dial code and contact preferences are controls inside the Cell Phone
  // and Email inputs, so they go out only when that input is on the form.
  // Otherwise (add_plan) the server keeps the patient's stored code and
  // choices, and the shared-patient cascade does not overwrite them.
  if (shownInMode('cell_phone', args)) {
    Object.assign(payload, {
      cellCountryCode: state.cellCountryCode,
      smsOptIn: state.smsOptIn,
      smsUnsubscribe: !state.marketingSms,
    });
  }
  if (shownInMode('email', args)) {
    Object.assign(payload, {
      emailOptIn: state.emailOptIn,
      emailUnsubscribe: !state.marketingEmails,
    });
  }

  return payload;
}

function shownInMode(key: string, args: BuildIntakePayloadArgs): boolean {
  const field = args.fields.find((f) => f.key === key);
  return field !== undefined && isShown(field, args.mode);
}
